import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { MdArrowBack } from 'react-icons/md';
import PageHeader from '../../../components/owner/PageHeader';
import PrintActionsHeader from '../../../components/shared/PrintActionsHeader';
import { useAuth } from '../../../context/AuthContext';
import { usePanelToast } from '../../../context/PanelToastContext';
import { formatGymPrice } from '../../../utils/gymMoney';
import { getGymMemberId } from '../members/memberData';
import {
  exportCustomerLedger,
  exportCustomerLedgerCsv,
  exportCustomerLedgerExcel,
  exportCustomerLedgerPdf,
  printCustomerLedger,
} from './exportCustomerLedger';
import { remainingPaymentCategory } from '../../receptionist/sales/remainingPaymentCategories';
import {
  saleRowLedgerCredit,
  saleRowLedgerDebit,
} from '../../receptionist/sales/salePaymentUtils';
import {
  fetchMemberById,
  fetchPaymentsForMember,
} from '../../../services/receptionist/receptionistApi';
import { fetchCustomerLedgerPageRpc } from '../../../services/shared/customerLedgerRpcApi';
import { setActiveGymId } from '../../../services/receptionist/gymContext';
import {
  getReceptionistBranchFilter,
  isReceptionistPanelScope,
} from '../../../services/gym/branchContext';
import { invalidateCache } from '../../../services/cache/queryCache';
import { PAYMENTS_UPDATE_EVENT } from '../../receptionist/paymentStore';
import '../../owner/ownerPages.css';
import '../../receptionist/members/members.css';
import '../adminListPages.css';
import './allModule.css';

function formatMoney(amount) {
  return formatGymPrice(amount);
}

function EmptyTable({ cols, message }) {
  return (
    <tr>
      <td colSpan={cols} className="owner-placeholder-text" style={{ textAlign: 'center', padding: '20px' }}>
        {message}
      </td>
    </tr>
  );
}

function SectionCard({ title, count, children }) {
  return (
    <div className="owner-card rec-panel-card admin-all-module-section">
      <div className="admin-all-module-section-header">
        <h2 className="owner-card-title">{title}</h2>
        {typeof count === 'number' && (
          <span className="admin-all-module-count">{count}</span>
        )}
      </div>
      {children}
    </div>
  );
}

function DetailGrid({ items }) {
  return (
    <dl className="admin-all-module-detail-grid">
      {items.map((item) => (
        <div key={item.label} className="admin-all-module-detail-item">
          <dt>{item.label}</dt>
          <dd>{item.value || '—'}</dd>
        </div>
      ))}
    </dl>
  );
}

/** Build chronological customer ledger: Sale = credit, Payment = debit, running balance. */
function buildCustomerLedgerEntries(payments) {
  const sorted = [...(payments || [])].sort((a, b) => {
    const dateCmp = String(a.date || '').localeCompare(String(b.date || ''));
    if (dateCmp !== 0) return dateCmp;
    return String(a.rawId || a.id || '').localeCompare(String(b.rawId || b.id || ''));
  });

  let running = 0;
  return sorted.map((row) => {
    // Remaining-clear rows are payment-only; original sale debit excludes later installments
    // merged into amount (those appear as separate remaining_clear rows).
    const credit = saleRowLedgerCredit(row);
    const debit = saleRowLedgerDebit(row);
    running = Math.round((running + credit - debit) * 100) / 100;
    const type = remainingPaymentCategory(row);
    const packageLabel = row.package || type || 'Sale';
    return {
      id: row.rawId || row.id,
      date: String(row.date || '').slice(0, 10) || '—',
      package: packageLabel,
      type,
      debit,
      credit,
      balance: running,
    };
  });
}

function CustomerLedgerTable({ entries }) {
  const totals = useMemo(() => (
    entries.reduce(
      (acc, row) => ({
        debit: acc.debit + (Number(row.debit) || 0),
        credit: acc.credit + (Number(row.credit) || 0),
      }),
      { debit: 0, credit: 0 },
    )
  ), [entries]);
  const closingBalance = entries.length ? entries[entries.length - 1].balance : 0;

  return (
    <div className="owner-table-wrap">
      <table className="owner-table rec-member-table admin-list-table admin-customer-ledger-table">
        <thead>
          <tr>
            <th>Date</th>
            <th>Package</th>
            <th>Payment (Debit)</th>
            <th>Sale (Credit)</th>
            <th>Balance</th>
          </tr>
        </thead>
        <tbody>
          {entries.length ? (
            <>
              {entries.map((row) => (
                <tr key={row.id}>
                  <td>{row.date}</td>
                  <td>
                    <div>{row.package}</div>
                    {row.type && row.type !== row.package && (
                      <small className="admin-customer-ledger-type">{row.type}</small>
                    )}
                  </td>
                  <td className={row.debit > 0 ? 'finance-amount-positive' : ''}>
                    {row.debit > 0 ? formatMoney(row.debit) : '—'}
                  </td>
                  <td>
                    {row.credit > 0 ? formatMoney(row.credit) : '—'}
                  </td>
                  <td className={row.balance > 0 ? 'finance-amount-negative' : 'finance-amount-positive'}>
                    {formatMoney(row.balance)}
                  </td>
                </tr>
              ))}
              <tr className="admin-customer-ledger-total-row">
                <td colSpan={2}><strong>Total</strong></td>
                <td><strong>{formatMoney(totals.debit)}</strong></td>
                <td><strong>{formatMoney(totals.credit)}</strong></td>
                <td className={closingBalance > 0 ? 'finance-amount-negative' : 'finance-amount-positive'}>
                  <strong>{formatMoney(closingBalance)}</strong>
                </td>
              </tr>
            </>
          ) : (
            <EmptyTable cols={5} message="No sales or payments in this ledger yet." />
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function CustomerLedgerDetail() {
  const { memberId } = useParams();
  const navigate = useNavigate();
  const toast = usePanelToast();
  const { account } = useAuth();
  const gymId = account?.gym_id || '';

  const [member, setMember] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState('');
  const [payments, setPayments] = useState([]);

  const loadData = useCallback(async () => {
    if (!gymId || !memberId) {
      setLoading(false);
      setLoadingDetails(false);
      setError(!gymId ? 'No gym is linked to this account.' : 'Member not found.');
      return;
    }
    try {
      setLoading(true);
      setLoadingDetails(true);
      setError('');
      setActiveGymId(gymId);
      const receptionist = isReceptionistPanelScope();
      const branch = receptionist
        ? String(getReceptionistBranchFilter() || '').trim()
        : '';

      const rpc = await fetchCustomerLedgerPageRpc({
        gymId,
        memberId,
        branch,
        receptionistScope: receptionist,
      });

      if (rpc?.notFound) {
        setMember(null);
        setPayments([]);
        setError('Member not found.');
        return;
      }

      if (rpc?.fromRpc && rpc.member) {
        setMember(rpc.member);
        setPayments(
          (rpc.payments || []).sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))),
        );
        return;
      }

      const found = await fetchMemberById(memberId);
      if (!found) {
        setMember(null);
        setPayments([]);
        setError('Member not found.');
        return;
      }
      setMember(found);
      const memberPayments = await fetchPaymentsForMember(found.id);
      setPayments(
        (memberPayments || []).sort((a, b) => String(a.date || '').localeCompare(String(b.date || ''))),
      );
    } catch (err) {
      setError(err.message || 'Failed to load member ledger');
      setMember(null);
      setPayments([]);
    } finally {
      setLoading(false);
      setLoadingDetails(false);
    }
  }, [gymId, memberId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    const refresh = () => {
      if (gymId) invalidateCache(`gym:${gymId}:customer-ledger:`);
      loadData();
    };
    window.addEventListener(PAYMENTS_UPDATE_EVENT, refresh);
    return () => window.removeEventListener(PAYMENTS_UPDATE_EVENT, refresh);
  }, [gymId, loadData]);

  const ledgerEntries = useMemo(() => buildCustomerLedgerEntries(payments), [payments]);

  const runLedgerExport = (exporterFn) => {
    try {
      exporterFn({ member, entries: ledgerEntries });
    } catch (err) {
      toast.showError(err.message || 'Export failed');
    }
  };

  if (loading) {
    return (
      <div>
        <PageHeader title="Customer Ledger" subtitle="Loading member…">
          <button type="button" className="owner-btn owner-btn-secondary" onClick={() => navigate('/admin/all-module')}>
            <MdArrowBack /> Back
          </button>
        </PageHeader>
        <p className="owner-placeholder-text">Loading…</p>
      </div>
    );
  }

  if (!member) {
    return (
      <div>
        <PageHeader title="Customer Ledger" subtitle={error || 'Member not found'}>
          <button type="button" className="owner-btn owner-btn-secondary" onClick={() => navigate('/admin/all-module')}>
            <MdArrowBack /> Back
          </button>
        </PageHeader>
      </div>
    );
  }

  return (
    <div>
      <PageHeader
        title={member.name || 'Customer Ledger'}
        subtitle={`${getGymMemberId(member) || '—'} · Customer ledger`}
      >
        <button type="button" className="owner-btn owner-btn-secondary" onClick={() => navigate('/admin/all-module')}>
          <MdArrowBack /> Back
        </button>
      </PageHeader>

      <PrintActionsHeader
        showDateFilter={false}
        onPrint={() => runLedgerExport(printCustomerLedger)}
        onCsv={() => runLedgerExport(exportCustomerLedgerCsv)}
        onPdf={() => runLedgerExport(exportCustomerLedgerPdf)}
        onExcel={() => runLedgerExport(exportCustomerLedgerExcel)}
        onExport={() => runLedgerExport(exportCustomerLedger)}
        ariaLabel="Customer ledger print and export"
      />

      {error && <p className="owner-placeholder-text owner-mb-16">{error}</p>}

      <SectionCard title="Member details">
        <DetailGrid
          items={[
            { label: 'Serial No', value: getGymMemberId(member) },
            { label: 'Name', value: member.name },
            { label: 'Phone', value: member.phone || member.mobile },
            { label: 'Email', value: member.email },
            { label: 'Branch', value: member.branch },
            { label: 'Package', value: member.package },
            { label: 'Status', value: member.status },
          ]}
        />
      </SectionCard>

      <SectionCard title="Ledger" count={ledgerEntries.length}>
        {loadingDetails ? (
          <p className="owner-placeholder-text">Loading ledger…</p>
        ) : (
          <CustomerLedgerTable entries={ledgerEntries} />
        )}
      </SectionCard>
    </div>
  );
}
